import JSZip from 'jszip';
import { FILE_NAME } from '@lib/spproject/constants';
import { type Manifest } from '@lib/spproject/types';
import { type BulkTreeItem, type ScannedFile } from '../types';

// TreeBuilder turns the files a user picks (or drops) into the in-memory tree
// the bulk-upload modal renders and uploads from, plus the small helpers that
// tree needs (path math, stats, manifest reading). Everything here is pure and
// framework-agnostic — no React, no network — so it stays easy to test.
//
// The file is ordered by importance:
//   1. Core — building the tree                (buildTreeFromFiles)
//   2. Tree utilities used by the hook/UI       (getParentPath, collectStats)
//   3. Drag & drop input gathering              (traverseDroppedEntry)
//   4. Reading a project's manifest at upload   (readManifestFromSPProject)
//   5. File-type classification + constants     (isProjectFile, ROOT_ID, …)

// ───────────────────────────────────────────────────────────────────────────
// 1. Core — building the tree
// ───────────────────────────────────────────────────────────────────────────

// The single most important function here: takes a flat list of scanned files
// and folds them into a nested folder/project/inspection tree.
//
// Example input — three files the user dropped:
//   "Istanbul/Bridge-A/pier-3.spproject"
//   "Istanbul/Bridge-A/pier-3.spinspection"
//   "Istanbul/notes.txt"
// becomes:
//   Istanbul/
//     Bridge-A/
//       pier-3.spproject     (project)
//       pier-3.spinspection  (inspection)
//   (notes.txt is ignored — not a .spproject/.spinspection)
//
// Intermediate folders are created on demand, then the tree is pruned of empty
// folders and sorted for display. id === path for every node — the rest of the
// feature relies on that (e.g. renaming rewrites both from the same string).

// Synthetic id of the invisible root folder that holds the top-level items.
export const ROOT_ID = '__root__';

export const isProjectFile = (name: string) => name.toLowerCase().endsWith('.spproject');
export const isInspectionFile = (name: string) => name.toLowerCase().endsWith('.spinspection');

export const buildTreeFromFiles = (files: ScannedFile[]): BulkTreeItem => {
  const root: BulkTreeItem = {
    id: ROOT_ID,
    type: 'folder',
    name: 'root',
    path: '',
    children: [],
    status: 'pending',
    selected: true,
  };

  for (const scannedFile of files) {
    // Split "Istanbul/Bridge-A/pier-3.spproject" into
    // ["Istanbul", "Bridge-A", "pier-3.spproject"].
    const pathSegments = scannedFile.relativePath.split('/').filter(Boolean);
    if (pathSegments.length === 0) continue;

    // Only .spproject / .spinspection files become leaves; everything else
    // (loose images, manifests, junk) is skipped here.
    const fileName = pathSegments[pathSegments.length - 1];
    if (!isProjectFile(fileName) && !isInspectionFile(fileName)) continue;

    // Walk down the folder names leading up to the file, creating each folder
    // the first time we see it ("Istanbul", then "Bridge-A").
    let currentFolder = root;
    let folderPath = '';

    for (let depth = 0; depth < pathSegments.length - 1; depth++) {
      const folderName = pathSegments[depth];
      folderPath += `/${folderName}`;

      let childFolder = currentFolder.children.find(
        child => child.type === 'folder' && child.name === folderName,
      );

      if (!childFolder) {
        childFolder = {
          id: folderPath,
          type: 'folder',
          name: folderName,
          path: folderPath,
          children: [],
          status: 'pending',
          selected: true,
        };
        currentFolder.children.push(childFolder);
      }

      currentFolder = childFolder;
    }

    // Attach the file as a leaf under its containing folder.
    currentFolder.children.push({
      id: `${folderPath}/${fileName}`,
      type: isProjectFile(fileName) ? 'project' : 'inspection',
      name: fileName,
      path: `${folderPath}/${fileName}`,
      file: scannedFile.file,
      children: [],
      status: 'pending',
      selected: true,
    });
  }

  pruneEmptyFolders(root);
  sortTree(root);

  return root;
};

// Drops folders that ended up with no project/inspection leaf beneath them, so
// the user isn't shown empty branches (e.g. an "Istanbul/" that only contained
// notes.txt). Returns whether the folder is kept (i.e. still has children) so
// parents can prune themselves in the same pass.
const pruneEmptyFolders = (folder: BulkTreeItem): boolean => {
  if (folder.type !== 'folder') return true;

  folder.children = folder.children.filter(child => {
    if (child.type === 'folder') return pruneEmptyFolders(child);
    return true;
  });

  return folder.children.length > 0;
};

// Display order within a folder: folders first, then projects, then
// inspections.
const typeWeight = (item: BulkTreeItem) => (item.type === 'folder' ? 0 : item.type === 'project' ? 1 : 2);

// Sorts every level of the tree by type, then name (case-insensitive).
const sortTree = (folder: BulkTreeItem) => {
  folder.children.sort((left, right) =>
    typeWeight(left) - typeWeight(right) || left.name.localeCompare(right.name)
  );

  folder.children.forEach(child => {
    if (child.type === 'folder') sortTree(child);
  });
};

// ───────────────────────────────────────────────────────────────────────────
// 2. Tree utilities used by the hook / UI
// ───────────────────────────────────────────────────────────────────────────

// Returns the parent folder path of a node path, or '' for a top-level node.
// e.g. "/Istanbul/Bridge-A/pier-3" -> "/Istanbul/Bridge-A", "/Istanbul" -> "".
// Used heavily by the upload orchestrator to find which folder a project or
// inspection belongs to.
export const getParentPath = (path: string): string => {
  const lastSlash = path.lastIndexOf('/');
  return lastSlash <= 0 ? '' : path.substring(0, lastSlash);
};

// Counts folders / projects / inspections in the tree (excluding the synthetic
// root) for the "X folders, Y projects, …" summary shown above the tree.
export const collectStats = (root: BulkTreeItem) => {
  let folders = 0;
  let projects = 0;
  let inspections = 0;

  const visit = (item: BulkTreeItem) => {
    if (item !== root && item.type === 'folder') folders++;
    if (item.type === 'project') projects++;
    if (item.type === 'inspection') inspections++;
    item.children.forEach(visit);
  };

  visit(root);

  return { folders, projects, inspections };
};

// ───────────────────────────────────────────────────────────────────────────
// 3. Drag & drop input gathering
// ───────────────────────────────────────────────────────────────────────────

// Recursively walks a dropped FileSystemEntry (the drag & drop counterpart of
// the folder <input>) and flattens it into the same ScannedFile[] that
// buildTreeFromFiles consumes. parentPath accumulates the relative path as we
// descend (e.g. "Istanbul/Bridge-A/").
export const traverseDroppedEntry = async (
  droppedEntry: FileSystemEntry,
  parentPath: string,
): Promise<ScannedFile[]> => {
  if (droppedEntry.isFile) {
    const file = await entryToFile(droppedEntry as FileSystemFileEntry);
    return [{ file, relativePath: `${parentPath}${droppedEntry.name}` }];
  }

  if (droppedEntry.isDirectory) {
    const reader = (droppedEntry as FileSystemDirectoryEntry).createReader();
    const childEntries = await readAllEntries(reader);
    const childParentPath = `${parentPath}${droppedEntry.name}/`;
    const collected: ScannedFile[] = [];

    for (const childEntry of childEntries) {
      collected.push(...await traverseDroppedEntry(childEntry, childParentPath));
    }

    return collected;
  }

  return [];
};

// readEntries() only returns a batch at a time, so keep calling it until it
// returns an empty batch to get every entry in a directory.
const readAllEntries = async (reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> => {
  const allEntries: FileSystemEntry[] = [];
  let batch: FileSystemEntry[] = [];

  do {
    batch = await readEntryBatch(reader);
    allEntries.push(...batch);
  } while (batch.length > 0);

  return allEntries;
};

// Promise wrappers around the callback-based File System Entry API.
const readEntryBatch = (reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> =>
  new Promise((resolve, reject) => reader.readEntries(resolve, reject));

const entryToFile = (fileEntry: FileSystemFileEntry): Promise<File> =>
  new Promise((resolve, reject) => fileEntry.file(resolve, reject));

// ───────────────────────────────────────────────────────────────────────────
// 4. Reading a project's manifest at upload time
// ───────────────────────────────────────────────────────────────────────────

// A .spproject is a zip; this reads and parses its manifest.json so the
// uploader knows the project id and which inspections it embeds. Throws if the
// manifest is missing (the caller surfaces that as a per-item error).
export const readManifestFromSPProject = async (file: File): Promise<Manifest.Manifest> => {
  const zip = await new JSZip().loadAsync(file);
  const manifestFile = zip.file(FILE_NAME.MANIFEST);

  if (!manifestFile) {
    throw new Error(`${FILE_NAME.MANIFEST} not found in ${file.name}`);
  }

  const content = await manifestFile.async('string');
  return JSON.parse(content) as Manifest.Manifest;
};

